import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  buildPortfolio,
  buildCoachingTriggers,
  DEMO_REPS as REPS,
  type CoachingTrigger,
  type PortfolioAccount,
} from "@/lib/extended";
import { api, getSession, getCurrentTenant } from "@/lib/session";
import { ArrowUpRight, Plus, Check, ChevronDown, ChevronUp } from "lucide-react";
import { DnaGlyph } from "@/components/DnaGlyph";
import { fetchRealPortfolio } from "@/pages/Pipeline";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Filter out simulation and smoke-test accounts by name prefix.
 * Accounts whose names start with "SIM-" or "SMOKE_TEST_" are internal
 * test fixtures and must not surface in any production coaching view.
 */
function isTestAccount(name: string): boolean {
  const n = name.trim();
  return n.startsWith("SIM-") || n.startsWith("SMOKE_TEST_");
}

// ─── L-003: 1:1 Prep helpers ────────────────────────────────────────────────

/**
 * Classify a coaching trigger message into a trigger type for template lookup.
 * Reads the message string since CoachingTrigger has no separate type field.
 */
function classifyTrigger(
  t: CoachingTrigger,
): "no-stakeholder-map" | "stuck" | "no-pain" | "no-activity" | "failed-gate" | "default" {
  const msg = t.message.toLowerCase();
  if (msg.includes("stakeholder map")) return "no-stakeholder-map";
  if (msg.includes("aging") || msg.includes("pushed")) return "stuck";
  if (msg.includes("no pain") || msg.includes("no-pain")) return "no-pain";
  if (msg.includes("no activity") || msg.includes("14+ days")) return "no-activity";
  if (msg.includes("cp") && (msg.includes("gate") || msg.includes("fail"))) return "failed-gate";
  return "default";
}

/**
 * Return a templated conversation starter for the manager to use in a 1:1.
 * Voice: authoritative-mentor — curious, forward-looking, not punitive.
 * CP names resolved from HELIX_CHECKPOINTS (canon source) — not hardcoded.
 */
function conversationStarter(t: CoachingTrigger): string {
  const type = classifyTrigger(t);
  const account = t.account;
  switch (type) {
    case "no-stakeholder-map":
      return `Walk me through who you have spent time with at ${account}. Who can sign? Who can block?`;
    case "stuck":
      return `Tell me about ${account} — what is the next concrete step and what is in the way?`;
    case "no-pain":
      return `What pain have you uncovered at ${account}? If none, what is your read on whether they have any?`;
    case "no-activity":
      return `What is happening with ${account}? Worth keeping it active or move to push?`;
    case "failed-gate": {
      const cpMatch = t.message.match(/CP(\d+)/i);
      const cpNum = cpMatch ? parseInt(cpMatch[1], 10) : null;
      const cpDef = cpNum !== null ? HELIX_CHECKPOINTS.find((c) => c.number === cpNum) : null;
      const cpRef = cpDef ? `${cpDef.fullName} on ` : cpMatch ? `CP${cpMatch[1]} on ` : "";
      return `Talk me through what happened at ${cpRef}${account}. What did you learn?`;
    }
    default:
      return `Walk me through where ${account} stands today and what your next move is.`;
  }
}

/**
 * Persist a trigger title to localStorage (cache) AND server.
 * Primary write: POST /api/coaching/agenda (server-authoritative).
 * Secondary write: localStorage cache (fast local read, survives offline).
 * key: next_1on1_{rep_id}, value: array of trigger titles
 */
function addTriggerToOneonOne(repId: string, repSlug: string, triggerTitle: string): void {
  // 1. localStorage cache (immediate, offline-safe)
  try {
    const key = `next_1on1_${repId}`;
    const existing = JSON.parse(localStorage.getItem(key) ?? "[]") as string[];
    if (!existing.includes(triggerTitle)) {
      existing.push(triggerTitle);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  } catch {
    // localStorage may be unavailable — silent fail
  }
  // 2. Server write (fire-and-forget — localStorage is the offline fallback)
  api<{ ok: boolean }>("/api/coaching/agenda", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rep_slug: repSlug, trigger_msg: triggerTitle }),
  }).catch(() => {
    // Server write failed — data remains in localStorage cache.
    // Will be visible on this device; lost on device switch until online.
  });
}

/**
 * Stable rep ID derived from the rep's name (slug form).
 */
function toRepId(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

function repSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "-");
}
function readRepScore(name: string): number | null {
  try {
    const raw = localStorage.getItem(`aip:pipeline-dna:${repSlug(name)}-score`);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// Minimal shape we need from the accounts API
interface ApiAccount {
  id: number;
  name: string;
}

// ─── CoachingCardCreatePanel ─────────────────────────────────────────────────
// Manager-only create/assign form for coaching notes.
// Reads reps from GET /api/users, posts to POST /api/coaching/note.

interface RepUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

function CoachingCardCreatePanel() {
  const [reps, setReps] = useState<RepUser[]>([]);
  const [repUserId, setRepUserId] = useState("");
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<"rep_visible" | "manager_only">("rep_visible");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api<RepUser[]>("/api/users")
      .then((data) => {
        const repList = Array.isArray(data)
          ? data.filter((u) => u.role === "ae" || u.role === "bdr")
          : [];
        setReps(repList);
        if (repList.length > 0) setRepUserId(repList[0].id);
      })
      .catch(() => setReps([]));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!repUserId || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api("/api/coaching/note", {
        method: "POST",
        body: JSON.stringify({ rep_user_id: repUserId, body: body.trim(), visibility }),
      });
      setSaved(true);
      setBody("");
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err?.message ?? "Failed to save note.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="rounded-xl border mb-6"
      style={{ borderColor: "hsl(var(--border))" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b flex items-center justify-between text-left"
        style={{ borderColor: "hsl(var(--border))" }}
        aria-expanded={open}
      >
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Manager action
          </div>
          <div className="text-sm font-semibold mt-0.5">
            Leave a coaching note for a rep
          </div>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
          : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
              Rep
            </label>
            {reps.length === 0 ? (
              <p className="text-sm text-muted-foreground">Loading reps...</p>
            ) : (
              <select
                value={repUserId}
                onChange={(e) => setRepUserId(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm bg-background"
              >
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.role.toUpperCase()})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
              Coaching note
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 500))}
              rows={4}
              placeholder="Write a focused coaching note. What did you observe? What should the rep work on?"
              className="rounded-lg border px-3 py-2 text-sm resize-none bg-background"
              maxLength={500}
            />
            <div className="text-[10px] text-right text-muted-foreground">{body.length}/500</div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
              Visibility
            </div>
            {(["rep_visible", "manager_only"] as const).map((v) => (
              <label key={v} className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="coaching_visibility"
                  value={v}
                  checked={visibility === v}
                  onChange={() => setVisibility(v)}
                  className="accent-[#DC2626]"
                />
                {v === "rep_visible" ? "Visible to rep" : "Manager only"}
              </label>
            ))}
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          {saved && (
            <p className="text-sm text-green-600">Note saved.</p>
          )}

          <button
            type="submit"
            disabled={saving || !repUserId || !body.trim()}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#DC2626] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            {saving ? "Saving..." : "Save coaching note"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function Coaching() {
  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const tenantId = session?.tenantId ?? 1;

  // D-020: detect BDR/AE role for role-aware render (evaluated after hooks)
  const isBdrRole = session?.role === "bdr";

  const [portfolio, setPortfolio] = useState<PortfolioAccount[]>([]);
  const [filterRep, setFilterRep] = useState<"all" | string>("all");
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  // Map from lowercased company name -> numeric DB account id
  const [accountIdMap, setAccountIdMap] = useState<Map<string, number>>(new Map());
  // L-003: 1:1 Prep panel state
  const [oneonOnePrepOpen, setOneonOnePrepOpen] = useState(true);
  const [oneonOneAdded, setOneonOneAdded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isDemo) {
      setPortfolio(buildPortfolio());
    } else {
      fetchRealPortfolio(tenantId)
        .then((p) => {
          // Filter out SIM-% and SMOKE_TEST_% test accounts from production views
          const live = p.filter((a) => !isTestAccount(a.company));
          setPortfolio(live);
        })
        .catch(() => setPortfolio([]));
    }
  }, [isDemo, tenantId]);

  // Load real accounts to build name -> numeric id lookup
  useEffect(() => {
    api<ApiAccount[]>("/api/accounts")
      .then((accounts) => {
        const m = new Map<string, number>();
        for (const a of accounts) {
          // Skip test accounts in the lookup map as well
          if (a.name && !isTestAccount(a.name)) m.set(a.name.toLowerCase().trim(), a.id);
        }
        setAccountIdMap(m);
      })
      .catch(() => {
        // Not signed in or API unavailable — coaching triggers still show, links disabled
      });
  }, []);

  const triggers = useMemo(() => buildCoachingTriggers(portfolio), [portfolio]);
  const filtered = useMemo(
    () => (filterRep === "all" ? triggers : triggers.filter((t) => t.rep === filterRep)),
    [triggers, filterRep],
  );

  const byRep = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of REPS) m[r] = 0;
    for (const t of triggers) m[t.rep] = (m[t.rep] ?? 0) + 1;
    return m;
  }, [triggers]);

  // L-003: per-rep top-3 coaching triggers for 1:1 Prep panel.
  // Severity order: high > medium > low. Within same severity, preserve list order.
  const severityRank: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const oneonOnePrepByRep = useMemo(() => {
    const allReps = isDemo ? (REPS as readonly string[]) : ["all"];
    const result: Record<string, CoachingTrigger[]> = {};
    for (const rep of allReps) {
      const repTriggers = triggers
        .filter((t) => (isDemo ? t.rep === rep : true))
        .sort((a, b) => (severityRank[a.severity] ?? 3) - (severityRank[b.severity] ?? 3))
        .slice(0, 3);
      result[rep] = repTriggers;
    }
    return result;
  }, [triggers, isDemo]);

  function addToAgenda(t: CoachingTrigger, idx: number) {
    setAdded((prev) => ({ ...prev, [`${idx}-${t.accountId}-${t.message}`]: true }));
    setToast("Added to next 1:1");
    window.setTimeout(() => setToast(null), 1800);
  }

  // Resolve numeric DB id for a coaching trigger.
  // The trigger's accountId is a slug; match via the account company name field.
  function resolveNumericId(t: CoachingTrigger): number | null {
    // t.account is the human-readable company name (e.g. "Partner", "Riverstone Capital")
    const key = t.account.toLowerCase().trim();
    return accountIdMap.get(key) ?? null;
  }

  // D-020: BDR and AE reps see coaching notes in their Daily page.
  if (isBdrRole || session?.role === "ae") {
    return (
      <OperatorShell breadcrumbs={[{ label: "Coaching" }]} pageTitle="Coaching" pageMeta="Coaching notes">
        <div className="card-shell p-8 text-center max-w-md mx-auto">
          <p className="text-sm text-muted-foreground">
            Your coaching notes from your manager appear in your Daily page.
          </p>
          <p className="text-sm text-muted-foreground mt-3">
            <Link href="/daily" className="underline underline-offset-2 text-foreground hover:text-[#DC2626] transition-colors">
              Go to your Daily page
            </Link>
          </p>
        </div>
      </OperatorShell>
    );
  }

  // Empty state for production tenants with no data yet
  // W36.1 FIX: W18CoachingPanels was not rendered in this early-return path.
  // Added W18CoachingPanels below the empty state card so the section always
  // renders for managers/executives regardless of whether portfolio data exists.
  if (!isDemo && portfolio.length === 0 && triggers.length === 0) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Coaching" }]} pageTitle="Coaching" pageMeta="Coaching triggers">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Coaching moments
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">What to coach this week</h1>
        </div>
        <div className="card-shell p-8 text-center max-w-lg mx-auto">
          <p className="text-sm font-medium text-foreground mb-2">No coaching triggers yet</p>
          <p className="text-sm text-muted-foreground">
            Coaching triggers appear automatically when AEs log activity against accounts — missing stakeholder maps, aging deals, no forward timeline, slippage. Ask your reps to log their first touches on active accounts to see triggers here.
          </p>
        </div>
        {/* ── W18 Coaching Panels always visible for manager/executive ──────── */}
        <W18CoachingPanels />
      </OperatorShell>
    );
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Coaching" }]} pageTitle="Coaching" pageMeta={isDemo ? "Coaching triggers · The Boss" : "Coaching triggers"}>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
          Coaching moments
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">What to coach this week</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Auto-generated from portfolio signals: missing stakeholder maps, aging deals, accounts without a forward timeline, slippage.
        </p>
      </div>

      {/* Manager create/assign coaching notes */}
      <CoachingCardCreatePanel />

      {/* Rep filter — demo only. Production tenants do not yet have rep ownership
          plumbed through fetchRealPortfolio (all triggers carry rep: "–"), so showing
          DEMO_REPS chips on a real tenant would leak Jack Smith / Misty Quota / Will
          Winsome onto a production surface. Hide them until owner-aware triggers ship. */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <FilterChip
          label={`All · ${triggers.length}`}
          active={filterRep === "all"}
          onClick={() => setFilterRep("all")}
        />
        {isDemo && REPS.map((r) => (
          <div key={r} className="inline-flex items-center gap-1.5">
            <a
              href={`#/pipeline-dna/${repSlug(r)}`}
              aria-label={`Open Pipeline DNA for ${r}`}
              className="inline-flex items-center justify-center rounded-md p-0.5 text-foreground/70 hover:text-foreground hover:bg-[hsl(var(--surface-2))] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40"
              onClick={(e) => e.stopPropagation()}
            >
              <DnaGlyph score={readRepScore(r)} size={20} ariaLabel={`${r} pipeline DNA`} />
            </a>
            <FilterChip
              label={`${r} · ${byRep[r] ?? 0}`}
              active={filterRep === r}
              onClick={() => setFilterRep(r)}
            />
          </div>
        ))}
      </div>

      {/* Trigger list */}
      <div data-tour="coaching-panel" className="space-y-2">
        {filtered.map((t, i) => {
          const key = `${i}-${t.accountId}-${t.message}`;
          const numericId = resolveNumericId(t);
          return (
            <div
              key={key}
              className="card-shell p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            >
              <div className={`w-1 self-stretch rounded-full ${
                t.severity === "high"
                  ? "bg-rose-400"
                  : t.severity === "medium"
                  ? "bg-orange-400"
                  : "bg-neutral-500"
              }`} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t.rep}
                </div>
                <div className="text-[14px] text-foreground mt-0.5">{t.message}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {numericId !== null ? (
                  <Link
                    href={`/account/${numericId}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] hover:border-[hsl(var(--accent-red))]/40 transition-colors"
                  >
                    Open account <ArrowUpRight className="w-3 h-3" />
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border border-border text-xs text-muted-foreground opacity-40 cursor-default"
                    title={`${t.account} is not in your live account list`}
                  >
                    {t.account} <ArrowUpRight className="w-3 h-3" />
                  </span>
                )}
                <button
                  onClick={() => addToAgenda(t, i)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border text-xs transition-colors ${
                    added[key]
                      ? "border-emerald-400/40 text-emerald-300"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]"
                  }`}
                >
                  {added[key] ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {added[key] ? "On agenda" : "Send to 1:1 agenda"}
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && filterRep !== "all" && (
          <div className="card-shell p-8 text-center">
            <p className="text-sm font-medium text-foreground mb-1">No triggers for this rep</p>
            <p className="text-sm text-muted-foreground">
              No coaching signals were detected for the selected rep this week. Use 1:1 time to coach forward strategy or build skills.
            </p>
          </div>
        )}
        {filtered.length === 0 && filterRep === "all" && (
          <div className="card-shell p-8 text-center">
            <p className="text-sm font-medium text-foreground mb-1">No coaching triggers this week</p>
            <p className="text-sm text-muted-foreground">
              No portfolio signals require attention right now. Use 1:1 time to coach forward strategy or build skills.
            </p>
          </div>
        )}
      </div>

      {/* L-003: 1:1 Prep panel */}
      <div className="mt-10">
        <button
          onClick={() => setOneonOnePrepOpen((v) => !v)}
          className="flex items-center gap-2 mb-4 text-left w-full group"
          aria-expanded={oneonOnePrepOpen}
          type="button"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground group-hover:text-foreground transition-colors">
            1:1 Prep
          </div>
          <div className="flex-1 border-t border-border" />
          {oneonOnePrepOpen
            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {oneonOnePrepOpen && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground -mt-2">
              Top 3 coaching triggers per rep with suggested conversation starters for your next 1:1.
            </p>

            {(isDemo ? (REPS as readonly string[]) : ["all"]).map((rep) => {
              const repTriggers = oneonOnePrepByRep[rep] ?? [];
              const repId = toRepId(rep);

              return (
                <div key={rep} className="card-shell p-5">
                  <h3 className="text-sm font-semibold tracking-tight mb-3">
                    {isDemo ? rep : "All reps"}
                  </h3>

                  {repTriggers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No coaching triggers detected this week. Use 1:1 time to coach forward strategy or build skills.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {repTriggers.map((t, ti) => {
                        const starter = conversationStarter(t);
                        const itemKey = `1on1-${rep}-${ti}-${t.accountId}`;
                        const wasAdded = oneonOneAdded[itemKey] ?? false;

                        return (
                          <li
                            key={itemKey}
                            className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4"
                          >
                            <div
                              className={`w-1 self-stretch rounded-full shrink-0 ${
                                t.severity === "high"
                                  ? "bg-rose-400"
                                  : t.severity === "medium"
                                  ? "bg-orange-400"
                                  : "bg-neutral-500"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-[14px] font-semibold text-foreground">
                                {t.message}
                              </span>
                              <span className="text-[14px] text-muted-foreground">
                                {" "}— Try: {starter}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                addTriggerToOneonOne(repId, repSlug(rep), t.message);
                                setOneonOneAdded((prev) => ({ ...prev, [itemKey]: true }));
                                setToast(`Added to next 1:1 with ${isDemo ? rep : "rep"}`);
                                window.setTimeout(() => setToast(null), 2000);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border text-xs transition-colors shrink-0 ${
                                wasAdded
                                  ? "border-emerald-400/40 text-emerald-300"
                                  : "border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]"
                              }`}
                              aria-label={`Add to next 1:1 with ${isDemo ? rep : "rep"}`}
                            >
                              {wasAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              {wasAdded ? "Added" : "Add to next 1:1"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── W18 Coaching Panels ─────────────────────────────────────────────── */}
      {/* Four structured panels per AE. Currently scaffolded with correct structure.
          TODO (W37+): wire each panel to real Supabase queries:
            - "What changed" → account_plan_mpact changes since last 1:1 timestamp
            - "Open actions" → cadence_actions WHERE assignee_id=ae_id AND completed_at IS NULL
            - "Skill gap heatmap" → account_answers grouped by CP, low-confidence CPs
            - "Next hook" → top account with biggest CP movement or oldest untouched
      */}
      <W18CoachingPanels />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 card-shell-2 px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </OperatorShell>
  );
}

// ─── W18 Coaching Panels ──────────────────────────────────────────────────────
// Four structured panels per coaching session:
//   1. What changed since last 1:1
//   2. Open actions
//   3. Skill gap heatmap
//   4. Next conversation hook
//
// TODO (W37+): wire to real Supabase queries. Currently scaffolded with the
// correct structure and a "No coaching items this cycle" empty state.

function W18CoachingPanels() {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-5">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          W18 · Coaching Summary
        </div>
        <div className="flex-1 border-t border-border" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Panel 1 — What changed since last 1:1 */}
        <div className="card-shell p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
            What changed since last 1:1
          </div>
          <div className="text-[14px] font-semibold tracking-tight mb-3">
            Account plan Composite score movement
          </div>
          {/*
            TODO (W37+): query account_plan_mpact for changes since the last
            recorded 1:1 timestamp for each AE. Compare composite score before
            and after the last 1:1 date. Render deltas per account.
            Query: SELECT account_id, mpact_before, mpact_after, delta
                   FROM account_plan_mpact_changes
                   WHERE ae_id = :ae_id AND changed_at > :last_1on1_at
                   ORDER BY abs(delta) DESC LIMIT 5;
          */}
          <p className="text-sm text-muted-foreground">
            No coaching items this cycle.
          </p>
        </div>

        {/* Panel 2 — Open actions */}
        <div className="card-shell p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
            Open actions
          </div>
          <div className="text-[14px] font-semibold tracking-tight mb-3">
            Assigned, not yet completed
          </div>
          {/*
            TODO (W37+): query cadence_actions WHERE assignee_id = ae_id
            AND completed_at IS NULL ORDER BY due_date ASC LIMIT 10.
            Render action title, due date, and account link.
          */}
          <p className="text-sm text-muted-foreground">
            No coaching items this cycle.
          </p>
        </div>

        {/* Panel 3 — Skill gap heatmap */}
        <div className="card-shell p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
            Skill gap heatmap
          </div>
          <div className="text-[14px] font-semibold tracking-tight mb-3">
            Checkpoints with low confidence
          </div>
          {/*
            TODO (W37+): query account_answers grouped by CP number for all
            active accounts owned by the AE. Compute average confidence per CP.
            Highlight CPs with avg score < 2 (Weak/Absent) as skill gaps.
            Query: SELECT cp_number, AVG(score) as avg_score, COUNT(*) as account_count
                   FROM account_answers JOIN accounts ON ... WHERE owner_user_id = ae_id
                   GROUP BY cp_number ORDER BY avg_score ASC;
          */}
          <p className="text-sm text-muted-foreground">
            No coaching items this cycle.
          </p>
        </div>

        {/* Panel 4 — Next conversation hook */}
        <div className="card-shell p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
            Next conversation hook
          </div>
          <div className="text-[14px] font-semibold tracking-tight mb-3">
            Top account to discuss
          </div>
          {/*
            TODO (W37+): surface the account with the biggest CP movement since
            the last 1:1, or the oldest account with no recent activity.
            Query: SELECT a.id, a.name, MAX(ABS(mpact_delta)) as movement
                   FROM accounts a LEFT JOIN account_plan_mpact m ON m.account_id = a.id
                   WHERE a.owner_user_id = ae_id GROUP BY a.id, a.name
                   ORDER BY movement DESC, a.updated_at ASC LIMIT 1;
          */}
          <p className="text-sm text-muted-foreground">
            No coaching items this cycle.
          </p>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
        active
          ? "border-[hsl(var(--accent-red))]/60 bg-[hsl(var(--accent-red))]/10 text-foreground"
          : "border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]"
      }`}
    >
      {label}
    </button>
  );
}
